import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react'
import type { Product } from '../../cpn-components/CpnProductCard/types.js'
import { HeaderNavigationMenu } from './types.js'
import type { UserProfile } from './types.js'

interface NavContextType {
  buildProps: {
    products?: Product[] // 产品数据
    categories?: Record<string, any> // 分类数据
  }
  cartCount: number
  event?: {
    search?: () => void
    cart?: () => void
    profile?: () => void
    livestream?: () => void
    join?: () => void
    login?: () => void
  }
  profile?: UserProfile // 用户信息
  isMobile?: boolean
  payloadData: any
  currentMenu?: HeaderNavigationMenu // 当前菜单
  setCurrentMenu?: (_menu: HeaderNavigationMenu) => void // 设置当前菜单
  currentSeriesMetadata?: any // 当前系列元数据
  setCurrentSeriesMetadata?: (_metadata: any) => void // 设置当前系列元数据
  subSubCategory?: any // 子子分类
  setSubSubCategory?: (_category: any) => void // 设置子子分类
  currentResourceMetadata?: any // 当前资源位元数据
  setCurrentResourceMetadata?: (_metadata: any) => void // 设置当前资源位元数据
  onSeriesProductClick?: (_seriesProduct: any, _position: number, _seriesLabel?: string) => void // 设置系列产品点击
  onSidebarNavClick?: (_series: any, _index: number, _subSubindex?: number) => void // 设置侧边栏导航点击
}

const initialState: NavContextType = {
  isMobile: false,
  payloadData: {},
  buildProps: {
    products: [],
    categories: {},
  },
  currentMenu: HeaderNavigationMenu.Primary,
  setCurrentMenu: () => {},
  currentSeriesMetadata: {},
  setCurrentSeriesMetadata: () => {},
  currentResourceMetadata: {},
  setCurrentResourceMetadata: () => {},
  onSeriesProductClick: (_seriesProduct: any, _position: number, _seriesLabel?: string) => {},
  onSidebarNavClick: (_series: any) => {},
  cartCount: 0,
}

type Action =
  | {
      type: 'SET_CURRENT_MENU'
      value: HeaderNavigationMenu
    }
  | {
      type: 'SET_CURRENT_SERIES_METADATA'
      value: any
    }
  | {
      type: 'SET_SUB_SUB_CATEGORY'
      value: any
    }
  | {
      type: 'SET_CURRENT_RESOURCE_METADATA'
      value: any
    }
export const NavContext = createContext<NavContextType>(initialState)

export const useNavContext = () => {
  const context = useContext(NavContext)
  if (!context) {
    throw new Error('useNavContext must be used in <NavProvider>')
  }
  return useContext(NavContext)
}

const reducer = (state: NavContextType, action: Action) => {
  switch (action.type) {
    case 'SET_CURRENT_MENU': {
      return {
        ...state,
        currentMenu: action.value,
      }
    }
    case 'SET_CURRENT_SERIES_METADATA': {
      return {
        ...state,
        currentSeriesMetadata: action.value,
      }
    }
    case 'SET_SUB_SUB_CATEGORY': {
      return {
        ...state,
        subSubCategory: action.value,
      }
    }
    case 'SET_CURRENT_RESOURCE_METADATA': {
      return {
        ...state,
        currentResourceMetadata: action.value,
      }
    }
  }
}

const NavProvider = ({
  children,
  ...rest
}: {
  children: React.ReactNode
} & NavContextType) => {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    currentMenu: HeaderNavigationMenu.Primary,
    setCurrentMenu: () => {},
  })

  const setCurrentMenu = useCallback(
    (value: HeaderNavigationMenu) => {
      return dispatch({ type: 'SET_CURRENT_MENU', value })
    },
    [dispatch]
  )

  const setCurrentSeriesMetadata = useCallback(
    (value: any) => {
      return dispatch({ type: 'SET_CURRENT_SERIES_METADATA', value })
    },
    [dispatch]
  )

  const setSubSubCategory = useCallback(
    (value: any) => {
      return dispatch({ type: 'SET_SUB_SUB_CATEGORY', value })
    },
    [dispatch]
  )

  const setCurrentResourceMetadata = useCallback(
    (value: any) => {
      return dispatch({ type: 'SET_CURRENT_RESOURCE_METADATA', value })
    },
    [dispatch]
  )

  const value = useMemo(
    () => ({
      ...state,
      setCurrentMenu,
      setCurrentSeriesMetadata,
      setSubSubCategory,
      setCurrentResourceMetadata,
    }),
    [setCurrentMenu, setCurrentSeriesMetadata, setSubSubCategory, setCurrentResourceMetadata, state]
  )

  return <NavContext.Provider value={{ ...value, ...rest }}>{children}</NavContext.Provider>
}

export default NavProvider
